/**
 * Pricing-card money formatting (PF-84). Pure — no React, no env — so it is
 * unit-tested here and copied byte-for-byte into the sibling sites
 * (mirror-parity guards the copies).
 *
 * Rules:
 * - A MISSING amount (`null` / `undefined`) has no figure at all: the caller
 *   hides the price row. Showing `0` would advertise a free plan that is not.
 * - An explicit `0` is a real price and is formatted like any other amount.
 * - The currency is ALWAYS the ISO 4217 code the API returns for the plan
 *   (the academy's student currency). Never a literal like `$`.
 * - Digits are pinned to Latin (`latn`). The sites have always shown Latin
 *   digits, and pinning them stops the server (Node ICU) and the visitor's
 *   browser from picking different digit sets for `ar`.
 */

export type Money = number | null | undefined

const isAmount = (amount: Money): amount is number => typeof amount === 'number' && Number.isFinite(amount)

export const formatMoney = (amount: Money, currency: string | null | undefined, locale: string): string | null => {
  if (!isAmount(amount)) return null

  const code = currency?.trim().toUpperCase() ?? ''
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      numberingSystem: 'latn',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    // Intl throws a RangeError for an empty or malformed currency code. Keep
    // the amount visible (with whatever code we have) rather than crash the
    // whole pricing section over one bad row.
    const number = new Intl.NumberFormat(locale, { numberingSystem: 'latn', maximumFractionDigits: 2 }).format(amount)
    return code ? `${number} ${code}` : number
  }
}

export type PlanPriceInput = {
  price: Money
  finalPrice: Money
  currency: string | null | undefined
}

export type PlanPriceDisplay = {
  /** The price the visitor pays, formatted; `null` = hide the price row. */
  final: string | null
  /** The struck-through pre-discount price; `null` = no strike-through. */
  original: string | null
}

export const getPlanPriceDisplay = (plan: PlanPriceInput, locale: string): PlanPriceDisplay => {
  const final = formatMoney(plan.finalPrice, plan.currency, locale)
  if (final === null) return { final: null, original: null }

  // Strike the original only when there is a real reduction to show. A plan
  // with a discount value but no effective change (e.g. discount type NONE)
  // would otherwise show the same number twice, once crossed out.
  const discounted = isAmount(plan.price) && isAmount(plan.finalPrice) && plan.price > plan.finalPrice
  return { final, original: discounted ? formatMoney(plan.price, plan.currency, locale) : null }
}
